// Composes the post-cut transformation video — what the barber sees on the
// reveal screen. Four input cases drive the layout choice:
//
//   video + video → simultaneous 9:16 stack. Top half = BEFORE, bottom half =
//                   AFTER. Both clips play at the same time and END together —
//                   we clip to MIN(beforeDur, afterDur) so the longer one
//                   gets cut off rather than the shorter one freezing on
//                   its last frame for awkward seconds. Output reads as a
//                   coherent transformation moment.
//
//   video + photo → simultaneous 9:16 stack. The photo half is static for
//                   the entire output; the video half plays normally.
//                   Output duration = the video's duration.
//
//   photo + video → mirror of the above.
//
//   photo + photo → sequential slideshow. 2.5s of fullscreen BEFORE → 2.5s
//                   of fullscreen AFTER (~5s total). Used so EVERY before+
//                   after combo produces a video output for uniform
//                   achievement counting.
//
// All branches output 1080×1920 portrait, 30 FPS. Audio is drawn from the
// AFTER clip when available, otherwise the BEFORE clip, otherwise silent.
// Mixing both audio tracks would be chaos (overlapping voices), so we pick
// one source — the after is the "reveal" moment so it's the natural choice.
//
// Implementation: canvas-driven render loop + canvas.captureStream(30) →
// MediaRecorder. Tries video/mp4;codecs=avc1 first (works on iOS Safari for
// share-sheet acceptance) and falls back to webm on Android/Chrome. No
// ffmpeg, no upload — Path 1 ephemeral all the way through.

const W = 1080;
const H = 1920;
const HALF_H = H / 2;
const FPS = 30;
// Target encoding bitrate for the stitched transformation video.
// 8 Mbps at 1080p portrait is the sweet spot for share quality vs.
// file size — well above the browser-default ~2.5 Mbps (which produces
// visible compression on hair detail + skin tones + clipper-pass
// motion), and well under the 20-50 Mbps the source iPhone/Android
// camera footage uses. At 8 Mbps a 30-second clip lands around 30 MB
// — comfortably under the 100 MB Reels / TikTok / Stories ceilings.
// Sets the floor on perceived quality; everything else (canvas
// resolution, fps, codec) was already tuned for this tier and was
// getting starved by the default bitrate.
const VIDEO_BITRATE = 8_000_000;
const PHOTO_SLIDESHOW_HALF_SEC = 2.5;
// Floor on stitched output duration. Reels + Facebook Reels reject
// videos shorter than 3s; we pad to 5s for a slight buffer + better
// transformation pacing. With the capture-time minimum at 5s already,
// this padding rarely fires — it's a safety net for legacy clips,
// edge cases, or future rule changes. Padding works because the
// render loop simply keeps drawing past the source video's natural
// end (browsers hold the last frame of a video element after
// playback ends), giving a freeze-frame extension. Audio cuts out
// at the source's natural end which is acceptable.
const MIN_OUTPUT_SEC = 3;
// Ceiling on stitched output duration. 60s is the binding cross-platform
// limit (Stories per-segment cap, YT Shorts default cap). The capture-
// time max is also 60s, so this cap rarely binds — it's belt-and-
// suspenders against legacy or edge-case inputs.
const MAX_OUTPUT_SEC = 60;
const NAVY_900 = '#0c1530';
const GOLD_500 = '#d4a942';

// Picture-frame overlay — same asset photoCompose.ts uses for stills, so
// the stitched video shares the BEFORE/AFTER + mascot chrome with the
// share composite. Pre-baked at 1080×1920 (matches the video output
// dimensions) so it's a single 1:1 drawImage per frame at no scale cost.
const FRAME_PORTRAIT_PATH = '/assets/frame-portrait.png';

/** Optional pan/zoom crop rect in native source-pixel coordinates.
 *  When set, only this region of the source is drawn into the
 *  composite half (cover-fitted to fill the dest strip). Lets the
 *  capture flow reposition the recording / photo without re-encoding
 *  the underlying source. Matches PhotoCropArea from PhotoCaptureModal. */
export type StitchCrop = { x: number; y: number; width: number; height: number };

export type StitchSource =
  | { kind: 'photo'; blob: Blob; crop?: StitchCrop | null }
  | { kind: 'video'; blob: Blob; crop?: StitchCrop | null };

export type StitchInput = {
  before: StitchSource;
  after: StitchSource;
  /** Progress 0→1 while the render loop runs. */
  onProgress?: (pct: number) => void;
};

/** Composes the transformation video from before + after sources. Returns
 *  a single video Blob the caller can hand to Web Share, save to disk, or
 *  preview inline. Throws if MediaRecorder / captureStream isn't supported. */
export async function stitchTransformation({ before, after, onProgress }: StitchInput): Promise<Blob> {
  const beforeMedia = await loadSource(before);
  const afterMedia = await loadSource(after);
  // Load the picture-frame overlay alongside the sources. Decorative —
  // .catch(null) so a missing/failed asset doesn't break the stitch;
  // the render loop simply skips the overlay draw in that case.
  const frameImg = await loadFrameOverlay().catch(() => null);

  const isPhotoOnly = before.kind === 'photo' && after.kind === 'photo';
  const outputDur = computeOutputDur(before, after, beforeMedia, afterMedia);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    cleanupSources([beforeMedia, afterMedia]);
    throw new Error('stitchTransformation: 2D context unavailable');
  }

  const captureStreamFn = (
    canvas as HTMLCanvasElement & { captureStream?: (fps: number) => MediaStream }
  ).captureStream;
  if (typeof captureStreamFn !== 'function') {
    cleanupSources([beforeMedia, afterMedia]);
    throw new Error('stitchTransformation: canvas.captureStream unavailable');
  }
  const stream = captureStreamFn.call(canvas, FPS);

  // Audio — prefer after, fall back to before. Safari's captureStream on
  // <video> elements returns a stream that includes the audio track once
  // the video starts playing. We pull the audio tracks off and add them
  // to the canvas stream so the recorder ends up with both video + audio.
  const audioMedia =
    after.kind === 'video' ? afterMedia : before.kind === 'video' ? beforeMedia : null;
  const audioStreamHandle = audioMedia ? attachAudio(audioMedia.element as HTMLVideoElement, stream) : null;

  const mime = pickStitchMime();
  // Pass videoBitsPerSecond explicitly. Browser MediaRecorder defaults
  // are ~2.5 Mbps (Chrome) to ~3 Mbps (Safari) at 1080p — well below
  // what canvas-driven 1080×1920 output needs to avoid visible
  // compression. See VIDEO_BITRATE constant above for rationale.
  const recorderOptions: MediaRecorderOptions = {
    videoBitsPerSecond: VIDEO_BITRATE,
    ...(mime ? { mimeType: mime } : {}),
  };
  const recorder = new MediaRecorder(stream, recorderOptions);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  // Start playback for any video sources. Muted on the SOURCE element is
  // fine — we already grabbed the audio stream off captureStream above,
  // and muting prevents a phantom playback through the device speakers
  // while we render.
  if (before.kind === 'video') void startVideo(beforeMedia.element as HTMLVideoElement);
  if (after.kind === 'video') void startVideo(afterMedia.element as HTMLVideoElement);

  return new Promise<Blob>((resolve, reject) => {
    let raf = 0;
    let stopped = false;

    const stopAll = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* ignore */
      }
      pauseSources([beforeMedia, afterMedia]);
    };

    recorder.onstop = () => {
      const out = new Blob(chunks, { type: mime || 'video/webm' });
      cleanupSources([beforeMedia, afterMedia]);
      audioStreamHandle?.cleanup?.();
      resolve(out);
    };
    recorder.onerror = (e) => {
      cleanupSources([beforeMedia, afterMedia]);
      audioStreamHandle?.cleanup?.();
      stopAll();
      reject(e);
    };

    recorder.start();
    const startTs = performance.now();

    const tick = () => {
      if (stopped) return;
      const elapsed = (performance.now() - startTs) / 1000;
      if (elapsed >= outputDur) {
        stopAll();
        return;
      }

      // Background fill — covers any letterboxing if cover-fit doesn't
      // fully fill (rare with our explicit dest sizes, but safe).
      ctx.fillStyle = NAVY_900;
      ctx.fillRect(0, 0, W, H);

      if (isPhotoOnly) {
        // Sequential slideshow. Half-and-half through the 5s window.
        // The frame's "BEFORE" + "AFTER" banners are both visible at
        // once (top + bottom of the frame), so we don't need to flip a
        // top-label between halves — the frame chrome stays put and
        // the photo underneath swaps from before to after at the
        // midpoint.
        const showAfter = elapsed >= PHOTO_SLIDESHOW_HALF_SEC;
        const src = showAfter ? afterMedia : beforeMedia;
        drawCoveredFromSource(ctx, src, 0, 0, W, H);
      } else {
        // Simultaneous 9:16 stack. Cover-fit each half independently so a
        // landscape-recorded source crops naturally to fit the 9:8 pane.
        // drawCoveredFromSource honors the optional crop set during
        // capture (PhotoCaptureModal / VideoCaptureModal) so panning +
        // zooming there changes what gets drawn here.
        drawCoveredFromSource(ctx, beforeMedia, 0, 0, W, HALF_H);
        drawCoveredFromSource(ctx, afterMedia, 0, HALF_H, W, HALF_H);
        // Gold seam between halves — interior divider so the eye reads
        // the two photos as two distinct moments. The outer chrome /
        // BEFORE / AFTER labels are part of the frame overlay drawn
        // below.
        ctx.fillStyle = GOLD_500;
        ctx.fillRect(0, HALF_H - 3, W, 6);
      }

      // Picture-frame overlay — last draw so it sits on top of both
      // halves + the seam. Single 1:1 drawImage at canvas size (the
      // asset is pre-baked at 1080×1920).
      if (frameImg) ctx.drawImage(frameImg, 0, 0, W, H);

      onProgress?.(Math.min(1, elapsed / outputDur));
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    // Safety net: if for some reason the loop hangs, force-stop the
    // recorder a second past the expected end. Catches devices that
    // throttle requestAnimationFrame in the background.
    window.setTimeout(stopAll, (outputDur + 1) * 1000);
  });
}

// --- helpers ---

type LoadedSource = {
  element: HTMLImageElement | HTMLVideoElement;
  url: string;
  /** Optional pan/zoom crop the user set during capture — see
   *  StitchCrop. Carries through to drawCoveredFromSource so the half-
   *  strip renders only that region of the source. */
  crop: StitchCrop | null;
};

async function loadSource(src: StitchSource): Promise<LoadedSource> {
  const url = URL.createObjectURL(src.blob);
  const crop = src.crop ?? null;
  if (src.kind === 'photo') {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image load failed'));
    });
    return { element: img, url, crop };
  }
  const video = document.createElement('video');
  video.src = url;
  video.playsInline = true;
  video.muted = true; // we capture audio separately via captureStream
  video.preload = 'auto';
  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('video load failed'));
  });
  return { element: video, url, crop };
}

function cleanupSources(items: LoadedSource[]) {
  for (const i of items) {
    URL.revokeObjectURL(i.url);
    if (i.element instanceof HTMLVideoElement) {
      try {
        i.element.pause();
        i.element.removeAttribute('src');
        i.element.load();
      } catch {
        /* ignore */
      }
    }
  }
}

function pauseSources(items: LoadedSource[]) {
  for (const i of items) {
    if (i.element instanceof HTMLVideoElement) {
      try {
        i.element.pause();
      } catch {
        /* ignore */
      }
    }
  }
}

async function startVideo(v: HTMLVideoElement) {
  v.currentTime = 0;
  try {
    await v.play();
  } catch {
    /* play() may reject in headless / background — fall through, the
       render loop will still draw the current frame */
  }
}

function attachAudio(
  source: HTMLVideoElement,
  destStream: MediaStream,
): { cleanup: () => void } | null {
  const captureFn = (
    source as HTMLVideoElement & { captureStream?: () => MediaStream; mozCaptureStream?: () => MediaStream }
  ).captureStream || (source as HTMLVideoElement & { mozCaptureStream?: () => MediaStream }).mozCaptureStream;
  if (typeof captureFn !== 'function') return null;
  const audioStream = captureFn.call(source);
  const tracks = audioStream.getAudioTracks();
  if (tracks.length === 0) return null;
  for (const t of tracks) destStream.addTrack(t);
  return {
    cleanup: () => {
      for (const t of tracks) {
        try {
          destStream.removeTrack(t);
          t.stop();
        } catch {
          /* ignore */
        }
      }
    },
  };
}

function computeOutputDur(
  before: StitchSource,
  after: StitchSource,
  beforeMedia: LoadedSource,
  afterMedia: LoadedSource,
): number {
  let computed: number;
  if (before.kind === 'photo' && after.kind === 'photo') {
    computed = PHOTO_SLIDESHOW_HALF_SEC * 2;
  } else if (before.kind === 'video' && after.kind === 'video') {
    const b = (beforeMedia.element as HTMLVideoElement).duration;
    const a = (afterMedia.element as HTMLVideoElement).duration;
    // Min, not max — both clips end together. The longer recording gets
    // tail-trimmed to match the shorter; nobody freezes on a last frame.
    computed = Math.min(safeDur(b), safeDur(a));
  } else {
    // Mixed: video duration drives the output.
    const v = before.kind === 'video' ? beforeMedia.element : afterMedia.element;
    computed = safeDur((v as HTMLVideoElement).duration);
  }
  // Clamp to [MIN_OUTPUT_SEC, MAX_OUTPUT_SEC]. Below the floor, the
  // render loop keeps drawing past the source's natural end, which
  // browsers handle by holding the last frame — gives a freeze-frame
  // extension. Above the ceiling, output is hard-capped.
  return Math.min(MAX_OUTPUT_SEC, Math.max(MIN_OUTPUT_SEC, computed));
}

function safeDur(d: number): number {
  if (!Number.isFinite(d) || d <= 0) return MIN_OUTPUT_SEC;
  return Math.min(d, MAX_OUTPUT_SEC);
}

/** Cover-fit a LoadedSource into the dest rect, honoring an optional
 *  capture-time crop rect. With crop: only the cropped region of the
 *  source is drawn (object-cover inside that region — exactly mirrors
 *  photoCompose.ts's drawHalf native-source path so still + video
 *  composites read identically). Without crop: full-source cover-fit
 *  (legacy behavior). */
function drawCoveredFromSource(
  ctx: CanvasRenderingContext2D,
  source: LoadedSource,
  dx: number,
  dy: number,
  dWidth: number,
  dHeight: number,
) {
  const el = source.element;
  const sw = el instanceof HTMLImageElement ? el.naturalWidth : el.videoWidth;
  const sh = el instanceof HTMLImageElement ? el.naturalHeight : el.videoHeight;
  if (!sw || !sh) {
    // Source not yet ready (e.g. video metadata not loaded). Paint a
    // navy fallback so the half doesn't flash transparent.
    ctx.fillStyle = NAVY_900;
    ctx.fillRect(dx, dy, dWidth, dHeight);
    return;
  }
  if (source.crop) {
    // Capture-time crop set: clamp to source bounds (defensive — the
    // crop coords come from react-easy-crop on the same source so they
    // should already be valid) and apply cover-fit inside the crop.
    const sr = clampRect(source.crop, sw, sh);
    const scale = Math.max(dWidth / sr.width, dHeight / sr.height);
    const innerW = dWidth / scale;
    const innerH = dHeight / scale;
    const sx = sr.x + (sr.width - innerW) / 2;
    const sy = sr.y + (sr.height - innerH) / 2;
    ctx.drawImage(el, sx, sy, innerW, innerH, dx, dy, dWidth, dHeight);
    return;
  }
  // No crop — original full-source cover-fit.
  const scale = Math.max(dWidth / sw, dHeight / sh);
  const dstW = sw * scale;
  const dstH = sh * scale;
  const ox = dx + (dWidth - dstW) / 2;
  const oy = dy + (dHeight - dstH) / 2;
  ctx.drawImage(el, 0, 0, sw, sh, ox, oy, dstW, dstH);
}

/** Clamp a crop rect to the source's natural dimensions so a bogus
 *  rect (from a torn capture state or older data) can't read past the
 *  source bounds and trigger DrawImage InvalidStateError. */
function clampRect(c: StitchCrop, sw: number, sh: number): StitchCrop {
  const x = Math.max(0, Math.min(c.x, sw));
  const y = Math.max(0, Math.min(c.y, sh));
  return {
    x,
    y,
    width: Math.max(1, Math.min(c.width, sw - x)),
    height: Math.max(1, Math.min(c.height, sh - y)),
  };
}

/** Load the picture-frame overlay PNG. Returns a fully-decoded
 *  HTMLImageElement ready for drawImage. The asset is cached by the
 *  browser after the first call so subsequent stitches don't re-hit
 *  the network. */
function loadFrameOverlay(): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('frame overlay load failed'));
    img.src = FRAME_PORTRAIT_PATH;
  });
}

// Try mp4 first (iOS Safari accepts this in Web Share); webm variants for
// Chrome / Firefox / Android. We honor whichever the device says it supports.
function pickStitchMime(): string {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return '';
}
